import { AnimatePresence, motion } from "motion/react"

import { CoachIdentity, STAGE_SPRING } from "@/components/coach-stage"
import { PageContainer } from "@/components/page-container"
import { greetingFor } from "@/lib/greeting"

import {
  ComposerSlot,
  ProgressPanel,
  RecentList,
  SpeakButton,
  TileRow,
  useModuleRows,
  type StartLayoutProps,
} from "./shared"

/**
 * Draft "Billboard": the hero image runs the full width, the coach sits in a
 * glass box on it, the modules follow as sideways-scrolling shelves.
 */
export function BillboardLayout(p: StartLayoutProps) {
  const rows = useModuleRows(p.recommendations)
  return (
    <>
      <section className="relative w-full overflow-hidden bg-[var(--surface-alt)]">
        <img
          src="/fotos/hero-hafencity.jpg"
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover object-center dark:opacity-60"
        />
        <div
          aria-hidden
          className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_0%,transparent_55%,var(--paper)_100%)]"
        />
        <div className="relative z-10 mx-auto flex min-h-[560px] w-full max-w-[1100px] items-end px-8 pt-24 pb-14">
          <motion.div
            layoutId="coach-card"
            transition={STAGE_SPRING}
            className="w-full max-w-[680px] rounded-2xl bg-[var(--paper)]/78 p-7 shadow-[0_24px_80px_rgba(31,31,31,0.18)] ring-1 ring-[var(--paper)]/80 backdrop-blur-2xl sm:p-8"
          >
            <AnimatePresence mode="wait" initial={false}>
              {p.voice ? (
                <motion.div
                  key="voice"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="-m-7 sm:-m-8 [&>div:last-child]:rounded-b-2xl"
                >
                  {p.voice}
                </motion.div>
              ) : (
                <motion.div
                  key="prompt"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <CoachIdentity coach={p.coach} size="lg" />
                  <h1 className="mt-6 font-display text-[32px] leading-[1.1] font-medium tracking-tight sm:text-[38px]">
                    {greetingFor(new Date())}
                    {p.firstName ? `, ${p.firstName}` : ""}.
                    <br />
                    <span className="text-foreground/65">Woran arbeiten wir heute?</span>
                  </h1>
                  {p.standHint ? (
                    <p className="mt-2.5 text-[13.5px] text-muted-foreground">
                      {p.standHint}
                    </p>
                  ) : null}
                  <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:items-stretch">
                    <ComposerSlot>{p.composer}</ComposerSlot>
                    <SpeakButton coachName={p.coachName} onSpeak={p.onSpeak} tone="filled" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <PageContainer className="mt-10 pb-14">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="min-w-0 space-y-9">
            {rows.map((row) => (
              <TileRow
                key={row.title}
                row={row}
                shape="wide"
                tileWidth="w-[300px]"
                set="fotos"
              />
            ))}
          </div>
          <aside className="space-y-8 lg:pt-11">
            <ProgressPanel
              resume={p.resume}
              completedCount={p.completedCount}
              accent="red"
            />
            <RecentList
              entries={p.entries}
              isLoading={p.entriesLoading}
              coachName={p.coachName}
            />
          </aside>
        </div>
      </PageContainer>
    </>
  )
}
